import { BKJButton, BKJCheckbox } from '@bookjane2/bookjane-design-library';
import { Flex } from 'components/Flex';
import { Portal } from 'components/Portal/Portal';
import { MODAL_ROOT_ID } from 'constants/elements';
import { useTranslation } from 'hooks/useTranslation';
import { getJ360ElementNode } from 'utils/getJ360ElementNode';
import { WhiteLabelAccessor } from 'utils/whiteLabelAccessor';
import { FC, useCallback, useEffect, useState } from 'react';
import {
  SMS_PRICING_BILLABLE_UNIT_ROWS,
  SMS_PRICING_BILLING_ITEMS,
  SMS_PRICING_DIALOG_TITLE_ID,
} from 'pages/Settings/pages/Organization/GeneralSettingsPage/SMSNotificationConfirmationDialog/SMSNotificationConfirmationDialog.constants';
import { SMSNotificationConfirmationDialogProps } from 'pages/Settings/pages/Organization/GeneralSettingsPage/SMSNotificationConfirmationDialog/SMSNotificationConfirmationDialog.types';
import {
  AcknowledgementLabel,
  DialogBackdrop,
  DialogBody,
  DialogCard,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Disclaimer,
  Eyebrow,
  ItemTitle,
  LastUpdated,
  Paragraph,
  PricingTable,
  SectionTitle,
  TableCell,
  TableHeaderCell,
} from 'pages/Settings/pages/Organization/GeneralSettingsPage/SMSNotificationConfirmationDialog/SMSNotificationConfirmationDialog.styled';

let targetElement = getJ360ElementNode(MODAL_ROOT_ID);

export const SMSNotificationConfirmationDialog: FC<SMSNotificationConfirmationDialogProps> = ({
  isOpen,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (!isOpen) setAcknowledged(false);
  }, [isOpen]);

  const handleKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    },
    [onCancel],
  );

  useEffect(() => {
    document.addEventListener('keyup', handleKeyUp);
    return () => document.removeEventListener('keyup', handleKeyUp);
  }, [handleKeyUp]);

  if (!isOpen) return null;

  if (!targetElement) {
    targetElement = document.createElement('div');
    targetElement.setAttribute('id', MODAL_ROOT_ID);
    document.body.appendChild(targetElement);
  }

  return (
    <Portal targetElement={targetElement}>
      <DialogCard role="dialog" aria-modal="true" aria-labelledby={SMS_PRICING_DIALOG_TITLE_ID}>
        <DialogHeader>
          <DialogTitle id={SMS_PRICING_DIALOG_TITLE_ID}>
            {t('settings:SMS_PRICING_TITLE')}
          </DialogTitle>
          <LastUpdated>{t('settings:SMS_PRICING_LAST_UPDATED')}</LastUpdated>
        </DialogHeader>

        <DialogBody>
          <Paragraph>{t('settings:SMS_PRICING_INTRO')}</Paragraph>

          <Flex flexDirection="column" gap="16px">
            <Eyebrow>{t('settings:SMS_PRICING_BILLING_EYEBROW')}</Eyebrow>
            <SectionTitle>{t('settings:SMS_PRICING_BILLING_TITLE')}</SectionTitle>
            <Paragraph>{t('settings:SMS_PRICING_BILLING_BODY')}</Paragraph>
            {SMS_PRICING_BILLING_ITEMS.map(({ titleKey, bodyKey }) => (
              <Flex key={titleKey} flexDirection="column" gap="6px">
                <ItemTitle>{t(titleKey)}</ItemTitle>
                <Paragraph>{t(bodyKey)}</Paragraph>
              </Flex>
            ))}
          </Flex>

          <Flex flexDirection="column" gap="16px">
            <Eyebrow>{t('settings:SMS_PRICING_BILLABLE_UNIT_EYEBROW')}</Eyebrow>
            <SectionTitle>{t('settings:SMS_PRICING_BILLABLE_UNIT_TITLE')}</SectionTitle>
            <Paragraph>{t('settings:SMS_PRICING_BILLABLE_UNIT_BODY')}</Paragraph>
            <PricingTable>
              <thead>
                <tr>
                  <TableHeaderCell scope="col">
                    {t('settings:SMS_PRICING_TABLE_SENDING')}
                  </TableHeaderCell>
                  <TableHeaderCell scope="col">
                    {t('settings:SMS_PRICING_TABLE_BILLED_AS')}
                  </TableHeaderCell>
                  <TableHeaderCell scope="col">
                    {t('settings:SMS_PRICING_TABLE_MEANING')}
                  </TableHeaderCell>
                </tr>
              </thead>
              <tbody>
                {SMS_PRICING_BILLABLE_UNIT_ROWS.map(
                  ({ sendingKey, billedAsKey, meaningKey, isBilled }) => (
                    <tr key={sendingKey}>
                      <TableCell $emphasis>{t(sendingKey)}</TableCell>
                      <TableCell $emphasis={isBilled} $muted={!isBilled}>
                        {t(billedAsKey)}
                      </TableCell>
                      <TableCell>{t(meaningKey)}</TableCell>
                    </tr>
                  ),
                )}
              </tbody>
            </PricingTable>
          </Flex>

          <Disclaimer>
            {t('settings:SMS_PRICING_DISCLAIMER', { brand: WhiteLabelAccessor.brandName })}
          </Disclaimer>
        </DialogBody>

        <DialogFooter>
          <AcknowledgementLabel>
            <BKJCheckbox
              variant="GreenSolid"
              value={acknowledged}
              name="smsPricingAgreementAcknowledged"
              onChange={() => setAcknowledged((prev) => !prev)}
            />
            {t('settings:SMS_PRICING_ACKNOWLEDGEMENT')}
          </AcknowledgementLabel>
          <Flex gap="12px" justifyContent="flex-end">
            <BKJButton variant="PurpleOutlined" width="140px" onClick={onCancel}>
              {t('auth:CANCEL')}
            </BKJButton>
            <BKJButton
              variant="PurpleSolid"
              width="140px"
              disabled={!acknowledged}
              onClick={onConfirm}
            >
              {t('auth:SAVE')}
            </BKJButton>
          </Flex>
        </DialogFooter>
      </DialogCard>
      <DialogBackdrop onClick={onCancel} />
    </Portal>
  );
};
